import { useEffect, useMemo, useState } from 'react';
import { fetchRawAllAppSettings } from './useFetchRawAllAppSettings';
import { fetchObjectTypeNamesToIds } from './useFetchObjectTypeNamesToIds';
import { FetchStatuses } from '../../types/FetchStatus';
const getAllAppSettingsByObjectTypeId = (rawAllAppSettings, objectTypeNamesToIds) => {
  return Object.keys(rawAllAppSettings).reduce((appSettings, objectTypeName) => {
    var _entry$crmPageEditor, _entry$crmPageEditor2;
    const typeInfo = objectTypeNamesToIds.get(objectTypeName);
    if (!typeInfo) return appSettings;
    const entry = rawAllAppSettings[objectTypeName];

    // typeInfo forms are nickname aware, incase a user renames an object
    // entry.crmPageEditor.metadata forms are static, they don't account for renames
    // If typeInfo forms exist, we overwrite the metadata forms to use those instead
    const shouldOverridePlural = typeInfo.pluralForm && ((_entry$crmPageEditor = entry.crmPageEditor) === null || _entry$crmPageEditor === void 0 || (_entry$crmPageEditor = _entry$crmPageEditor.metadata) === null || _entry$crmPageEditor === void 0 ? void 0 : _entry$crmPageEditor.pluralForm);
    const shouldOverrideSingular = typeInfo.singularForm && ((_entry$crmPageEditor2 = entry.crmPageEditor) === null || _entry$crmPageEditor2 === void 0 || (_entry$crmPageEditor2 = _entry$crmPageEditor2.metadata) === null || _entry$crmPageEditor2 === void 0 ? void 0 : _entry$crmPageEditor2.singularForm);
    if (shouldOverridePlural || shouldOverrideSingular) {
      const overriddenMetadata = Object.assign({}, entry.crmPageEditor.metadata);
      if (shouldOverridePlural) {
        const pluralFormMetadata = entry.crmPageEditor.metadata.pluralForm;
        overriddenMetadata.pluralForm = {
          source: pluralFormMetadata.source,
          isPortalSpecific: pluralFormMetadata.isPortalSpecific,
          value: typeInfo.pluralForm
        };
      }
      if (shouldOverrideSingular) {
        const singularFormMetadata = entry.crmPageEditor.metadata.singularForm;
        overriddenMetadata.singularForm = {
          source: singularFormMetadata.source,
          isPortalSpecific: singularFormMetadata.isPortalSpecific,
          value: typeInfo.singularForm
        };
      }
      const overriddenEntry = Object.assign({}, entry, {
        crmPageEditor: Object.assign({}, entry.crmPageEditor, {
          metadata: overriddenMetadata
        })
      });
      appSettings.set(typeInfo.objectTypeId, overriddenEntry);
    } else {
      appSettings.set(typeInfo.objectTypeId, entry);
    }
    return appSettings;
  }, new Map());
};
export const fetchAllAppSettings = () => {
  const allRawAppSettingsPromise = fetchRawAllAppSettings();
  const objectTypeNamesToIdsPromise = fetchObjectTypeNamesToIds();
  return Promise.all([allRawAppSettingsPromise, objectTypeNamesToIdsPromise]).then(([rawAllAppSettings, objectTypeNamesToIds]) => {
    return getAllAppSettingsByObjectTypeId(rawAllAppSettings, objectTypeNamesToIds);
  });
};
export const useFetchAllAppSettings = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState();
  useEffect(() => {
    fetchAllAppSettings().then(result => {
      setData(result);
    }).catch(err => setError(err)).finally(() => setLoading(false));
  }, []);
  return useMemo(() => {
    if (error) {
      return {
        data: null,
        status: FetchStatuses.REJECTED,
        error
      };
    }

    // Combined LOADING state
    if (loading) {
      return {
        data: null,
        status: FetchStatuses.LOADING
      };
    }
    if (data) {
      return {
        data,
        status: FetchStatuses.RESOLVED
      };
    }

    // Combined IDLE state
    return {
      data: null,
      status: FetchStatuses.IDLE
    };
  }, [error, loading, data]);
};